// ────────────────────────────────────────────────────────────────────
// PidHoverCard — a floating detail card shown while hovering an asset/line on
// the integrity-map P&ID. Reads only the already-loaded asset-list fields (no
// fetch on hover → instant), so it's a quick "what is this" peek; the full
// verdict/history lives on the asset page (click to open).
// ────────────────────────────────────────────────────────────────────

import type { Asset } from "@/lib/types";

const STATUS: Record<string, { label: string; color: string }> = {
  crit: { label: "Critical", color: "var(--err)" },
  warn: { label: "Warn", color: "var(--warn)" },
  watch: { label: "Watch", color: "#CA8A04" },
  ok: { label: "OK", color: "var(--ok)" },
  "": { label: "No data", color: "#9CA3AF" },
};

const CARD_W = 264;

export function PidHoverCard({ asset, x, y }: { asset: Asset; x: number; y: number }): JSX.Element {
  const s = STATUS[asset.worst_cml_status] ?? STATUS[""];
  // Flip left / clamp up so the card never runs off the viewport edge.
  const flipLeft = x > window.innerWidth - (CARD_W + 32);
  const left = flipLeft ? x - CARD_W - 16 : x + 16;
  const top = Math.min(y + 16, window.innerHeight - 210);

  return (
    <div
      className="fixed z-30 pointer-events-none"
      style={{
        left, top, width: CARD_W,
        background: "rgba(251,250,245,0.98)",
        border: "1px solid var(--hairline)",
        borderLeft: `3px solid ${s.color}`,
        boxShadow: "0 6px 24px rgba(10,22,40,0.16)",
      }}
    >
      <div className="px-3 pt-2.5 pb-2">
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-flex items-center font-mono uppercase px-1.5 py-0.5 shrink-0"
            style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".06em", color: "#fff", background: s.color }}>
            {s.label}
          </span>
          <span className="font-mono truncate" style={{ fontSize: 13, fontWeight: 700, letterSpacing: ".04em", color: "var(--accent-deep)" }}>
            {asset.tag}
          </span>
        </div>
        {asset.name && asset.name !== asset.tag && (
          <div className="font-sans mb-2" style={{ fontSize: 12.5, color: "var(--ink-1)", lineHeight: 1.3 }}>{asset.name}</div>
        )}
        <dl className="grid gap-x-3 gap-y-1 font-mono" style={{ gridTemplateColumns: "auto 1fr", fontSize: 10.5 }}>
          <Row k="Class" v={asset.asset_class} />
          {asset.service && <Row k="Service" v={asset.service} />}
          {asset.design_code && <Row k="Code" v={asset.design_code} />}
          {asset.corrosion_loop && <Row k="Loop" v={asset.corrosion_loop} />}
          {asset.damage_mechanism && <Row k="DM" v={asset.damage_mechanism} />}
          <Row k="CMLs" v={`${asset.cml_count}${asset.inspection_count ? ` · ${asset.inspection_count} insp` : ""}`} />
          {asset.latest_inspection_date && <Row k="Last insp" v={asset.latest_inspection_date} />}
        </dl>
      </div>
      <div className="px-3 py-1.5 font-mono uppercase"
        style={{ fontSize: 8.5, letterSpacing: ".14em", color: "var(--ink-4)", borderTop: "1px solid var(--hairline)" }}>
        Click to open asset →
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <>
      <dt className="uppercase" style={{ color: "var(--ink-4)", letterSpacing: ".08em" }}>{k}</dt>
      <dd className="m-0 truncate" style={{ color: "var(--ink-2)" }}>{v}</dd>
    </>
  );
}
